/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { Sparkline } from "@/components/common/Sparkline";
import type { AdGuardStats } from "@/api/adguard";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";

const compact = (n: number) => (n >= 10_000 ? `${(n / 1000).toFixed(n >= 100_000 ? 0 : 1)}k` : `${n}`);

/** DNS filtering over the last day, from AdGuard Home. */
export const AdGuardWidget: React.FC<{ stats: AdGuardStats }> = ({ stats }) => {
  const pct = stats.queries ? (stats.blocked / stats.queries) * 100 : 0;
  const blocked = stats.topBlocked ?? [];
  const peak = Math.max(1, ...blocked.map((b) => b.count));

  return (
    <section className="gh-w">
      <div className="gh-w-head">
        <span className="gh-label">DNS filtering</span>
        <span className="gh-sub">AdGuard · 24 h</span>
      </div>
      {stats.error ? (
        <div className="gh-empty">{stats.error}</div>
      ) : (
        <>
          <div className="gh-net-now">
            <div className="gh-net-fig gh-net-fig--rx">
              <span className="gh-big"><AnimatedNumber value={stats.queries} format={(n) => compact(Math.round(n))} /></span>
              <span>QUERIES</span>
            </div>
            <div className="gh-net-fig">
              <span className="gh-big"><AnimatedNumber value={pct} format={(n) => `${n.toFixed(n >= 10 ? 0 : 1)}%`} /></span>
              <span>BLOCKED</span>
            </div>
          </div>
          {(stats.series ?? []).length > 1 && (
            <div className="gh-spark gh-net-chart">
              <Sparkline values={stats.series ?? []} color="#5fa37a" height={34} smooth={2} format={(v) => `${v}`} />
            </div>
          )}
          <ul className="dns-top">
            {blocked.map((b) => (
              <li key={b.domain}>
                <i style={{ width: `${(b.count / peak) * 100}%` }} />
                <span>{b.domain}</span>
                <b>{b.count}</b>
              </li>
            ))}
          </ul>
          <div className="gh-net-meta">
            <span>{`avg answer ${stats.avgMs.toFixed(0)} ms`}</span>
          </div>
        </>
      )}
    </section>
  );
};
